// File-upload field. Delegates the actual persistence to the host-supplied
// `uploadAdapter` (see src/adapters/tempAttachment.ts for the default Rails
// adapter). Each in-flight upload gets its own AbortController so it can be
// cancelled individually and is aborted if the field unmounts. Completed files
// live in the store as UploadFileNode[]; in-flight uploads are local state.

import { useEffect, useRef, useState } from 'react'
import { FieldShell } from '../FieldShell'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import type { UploadConfig, UploadFileNode } from '../types'
import { errorIdFor } from '../ids'
import { newRowKey } from '../value'
import { useControlMeta } from './useControlMeta'

interface InFlightUpload {
  localId: string
  name: string
  size: number
  progress: number
  error?: string
  controller: AbortController
}

function asFiles(value: unknown): UploadFileNode[] {
  return Array.isArray(value) ? (value as UploadFileNode[]) : []
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Normalize stored accepted_types ("pdf", "*.pdf", ".pdf") into accept-attr extensions. */
function acceptAttr(types: string[] | undefined): string | undefined {
  if (!types?.length) return undefined
  return types
    .map((t) => `.${t.trim().toLowerCase().replace(/^\*?\.?/, '')}`)
    .filter((t) => t.length > 1)
    .join(',')
}

function nameMatches(name: string, types: string[] | undefined): boolean {
  if (!types?.length) return true
  const lower = name.toLowerCase()
  return types.some((t) => {
    const ext = t.trim().toLowerCase().replace(/^\*?\.?/, '')
    return ext.length > 0 && lower.endsWith(`.${ext}`)
  })
}

export function UploadField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const { uploadAdapter, errors: allErrors } = useFieldContext()
  const config = field.config as UploadConfig | undefined
  const files = asFiles(value)

  const [inFlight, setInFlight] = useState<InFlightUpload[]>([])
  // Latest committed files, kept in a ref so concurrent upload resolutions
  // append to each other rather than racing on a stale snapshot.
  const filesRef = useRef<UploadFileNode[]>(files)
  filesRef.current = files
  // All live controllers, aborted on unmount.
  const controllersRef = useRef<Set<AbortController>>(new Set())

  useEffect(() => {
    const controllers = controllersRef.current
    return () => {
      controllers.forEach((c) => c.abort())
      controllers.clear()
    }
  }, [])

  const max = typeof config?.max_items === 'number' ? config.max_items : Infinity
  const total = files.length + inFlight.filter((u) => !u.error).length
  const atMax = total >= max
  const canUpload = !meta.disabled && uploadAdapter != null && !atMax

  const updateInFlight = (localId: string, patch: Partial<InFlightUpload>) =>
    setInFlight((prev) => prev.map((u) => (u.localId === localId ? { ...u, ...patch } : u)))

  const removeInFlight = (localId: string) =>
    setInFlight((prev) => prev.filter((u) => u.localId !== localId))

  const appendFile = (node: UploadFileNode) => {
    const next = [...filesRef.current, node]
    filesRef.current = next
    meta.setFieldValue(path, next)
    meta.notifyBlur(path)
  }

  const removeFile = (id: string) => {
    const next = filesRef.current.filter((f) => f.id !== id)
    filesRef.current = next
    meta.setFieldValue(path, next)
    meta.notifyBlur(path)
  }

  const startUpload = (file: File) => {
    if (uploadAdapter == null) return
    const localId = newRowKey()

    // Client-side guards mirror the validator so the user sees immediate feedback.
    // max_size <= 0 is treated as "no limit" (legacy convention).
    if (typeof config?.max_size === 'number' && config.max_size > 0 && file.size > config.max_size) {
      setInFlight((prev) => [
        ...prev,
        { localId, name: file.name, size: file.size, progress: 0, error: 'file is too large', controller: new AbortController() },
      ])
      return
    }
    if (!nameMatches(file.name, config?.accepted_types)) {
      setInFlight((prev) => [
        ...prev,
        { localId, name: file.name, size: file.size, progress: 0, error: 'file type is not allowed', controller: new AbortController() },
      ])
      return
    }

    const controller = new AbortController()
    controllersRef.current.add(controller)
    setInFlight((prev) => [
      ...prev,
      { localId, name: file.name, size: file.size, progress: 0, controller },
    ])

    uploadAdapter(file, field, {
      signal: controller.signal,
      onProgress: (loaded, t) => updateInFlight(localId, { progress: t > 0 ? loaded / t : 0 }),
    })
      .then((node) => {
        controllersRef.current.delete(controller)
        removeInFlight(localId)
        appendFile(node)
      })
      .catch((err: unknown) => {
        controllersRef.current.delete(controller)
        // Abort (cancel or unmount) is not a validation error — just drop the row.
        if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
          removeInFlight(localId)
          return
        }
        updateInFlight(localId, { error: err instanceof Error ? err.message : 'Upload failed' })
      })
  }

  const onSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? [])
    const room = max - total
    selected.slice(0, Number.isFinite(room) ? Math.max(room, 0) : selected.length).forEach(startUpload)
    // Reset so selecting the same file again re-triggers change.
    event.target.value = ''
  }

  const cancelUpload = (upload: InFlightUpload) => {
    upload.controller.abort()
    controllersRef.current.delete(upload.controller)
    removeInFlight(upload.localId)
  }

  // The label points at the file input with `htmlFor` when there is one to point
  // at; when uploads are closed (disabled / at max / no adapter) no input
  // renders, so the label falls back to naming the field via aria instead of
  // carrying a `for` that resolves to nothing.
  return (
    <FieldShell field={field} path={path} errors={errors} groupLabel={!canUpload}>
      {canUpload ? (
        <input
          id={meta.id}
          type="file"
          className={`form-control${meta.hasErrors ? ' is-invalid' : ''}`}
          multiple={config?.multiple === true}
          accept={acceptAttr(config?.accepted_types)}
          aria-labelledby={meta.labelId}
          aria-describedby={meta.describedBy}
          aria-invalid={meta.ariaInvalid}
          aria-required={meta.ariaRequired}
          onChange={onSelect}
        />
      ) : uploadAdapter == null && !meta.disabled ? (
        <div className="form-text text-warning" role="status">
          Uploads are unavailable (no upload adapter configured).
        </div>
      ) : null}

      {files.length > 0 || inFlight.length > 0 ? (
        <ul className="list-group mt-2">
          {files.map((file, i) => {
            const fileErrors = allErrors[`${path}[${i}]`] ?? []
            return (
              <li className="list-group-item d-flex justify-content-between align-items-center" key={file.id}>
                <span>
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>{' '}
                  <small className="text-muted">{formatSize(file.size)}</small>
                  {fileErrors.length ? (
                    <span id={errorIdFor(`${path}[${i}]`)} className="text-danger small d-block">
                      {fileErrors.map((e) => e.message).join(', ')}
                    </span>
                  ) : null}
                </span>
                {meta.disabled ? null : (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeFile(file.id)}
                  >
                    Remove
                  </button>
                )}
              </li>
            )
          })}
          {inFlight.map((upload) => (
            <li className="list-group-item" key={upload.localId}>
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  {upload.name} <small className="text-muted">{formatSize(upload.size)}</small>
                </span>
                {upload.error ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={`Dismiss ${upload.name}`}
                    onClick={() => removeInFlight(upload.localId)}
                  >
                    Dismiss
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={`Cancel ${upload.name}`}
                    onClick={() => cancelUpload(upload)}
                  >
                    Cancel
                  </button>
                )}
              </div>
              {upload.error ? (
                <div className="text-danger small">{upload.error}</div>
              ) : (
                <div
                  className="progress mt-1"
                  role="progressbar"
                  aria-label={`Uploading ${upload.name}`}
                  aria-valuenow={Math.round(upload.progress * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="progress-bar" style={{ width: `${Math.round(upload.progress * 100)}%` }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </FieldShell>
  )
}
